import { parseDate, startOfDay } from "./format.ts";
import type { Item, Project } from "./types";

// --- Item dependencies ------------------------------------------------------

export function prereqOf(item: Item, byId: Map<string, Item>): Item | undefined {
  return item.dependsOn ? byId.get(item.dependsOn) : undefined;
}

/** True when this item can't be completed yet because its prerequisite isn't done. */
export function isBlockedByDeps(item: Item, byId: Map<string, Item>): boolean {
  const p = prereqOf(item, byId);
  return !!p && p.status !== "done";
}

export function blockingLabel(item: Item, byId: Map<string, Item>): string | undefined {
  const p = prereqOf(item, byId);
  return p && p.status !== "done" ? p.title : undefined;
}

const MS_DAY = 86400000;

export interface RiskReading {
  /** The open prerequisite that threatens this item's date. */
  blocker: Item;
  /** Days the blocker is itself overdue; 0 when it is not yet late. */
  daysLate: number;
  message: string;
}

/**
 * Slip risk (§12). One rule, deliberately: an item is at risk when its
 * `dependsOn` target is still open AND that blocker is due on or after the
 * item's own due date. Full critical-path math is out of scope.
 *
 * Shared by the drawer banner, the Dashboard register and mobile cards, so the
 * three can never disagree about what "at risk" means.
 */
export function riskOf(item: Item, byId: Map<string, Item>): RiskReading | undefined {
  const blocker = prereqOf(item, byId);
  if (!blocker || blocker.status === "done") return undefined;

  const mine = parseDate(item.due);
  const theirs = parseDate(blocker.due);
  if (!mine || !theirs) return undefined;
  if (theirs < mine) return undefined; // blocker lands first — the date holds

  const today = startOfDay();
  const daysLate = theirs < today ? Math.round((today.getTime() - theirs.getTime()) / MS_DAY) : 0;
  return {
    blocker,
    daysLate,
    message: daysLate > 0
      ? `Blocker is ${daysLate} day${daysLate === 1 ? "" : "s"} late`
      : "Blocker is due on or after this date",
  };
}

/** Items that depend on the given item (reverse edges). */
export function dependentsOf(itemId: string, all: Item[]): Item[] {
  return all.filter((i) => i.dependsOn === itemId);
}

export function itemsById(all: Item[]): Map<string, Item> {
  return new Map(all.map((i) => [i.id, i]));
}

// --- Project hierarchy ------------------------------------------------------

export interface ProjectNode {
  project: Project;
  /** Sub-projects, each with their own — as deep as Todoist nests them. */
  children: ProjectNode[];
}

/**
 * Projects as a tree, at any depth. Top-level = no parent, or a parent that
 * isn't itself in the list (archived, filtered out), so nothing goes missing.
 *
 * It used to stop at one level: a project under a sub-project was filed under
 * a node that was never rendered, and vanished from every list.
 */
export function projectTree(projects: Project[]): ProjectNode[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const childrenOf = new Map<string, Project[]>();
  const roots: Project[] = [];

  for (const p of projects) {
    if (p.parent && byId.has(p.parent) && p.parent !== p.id) {
      if (!childrenOf.has(p.parent)) childrenOf.set(p.parent, []);
      childrenOf.get(p.parent)!.push(p);
    } else {
      roots.push(p);
    }
  }

  const placed = new Set<string>();
  const build = (project: Project): ProjectNode => {
    placed.add(project.id);
    return {
      project,
      // `placed` guards against a cycle (A under B under A) looping forever.
      children: (childrenOf.get(project.id) || []).filter((c) => !placed.has(c.id)).map(build),
    };
  };
  const tree = roots.map(build);
  // Anything only reachable through a cycle has no root above it; show it at
  // the top rather than lose it.
  for (const p of projects) if (!placed.has(p.id)) tree.push(build(p));
  return tree;
}

/** A tree's projects in display order, each with how deep it sits (0 = top). */
export function flattenTree(nodes: ProjectNode[], depth = 0): { project: Project; depth: number; hasChildren: boolean }[] {
  return nodes.flatMap((n) => [
    { project: n.project, depth, hasChildren: n.children.length > 0 },
    ...flattenTree(n.children, depth + 1),
  ]);
}

/** All descendant ids of a project (to prevent choosing them as its own parent). */
export function descendantIds(projectId: string, projects: Project[]): Set<string> {
  const out = new Set<string>();
  const walk = (id: string) => {
    for (const p of projects) {
      if (p.parent === id && !out.has(p.id)) {
        out.add(p.id);
        walk(p.id);
      }
    }
  };
  walk(projectId);
  return out;
}
